import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import {
  Bell,
  Send,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertCircle,
  Phone,
  PhoneOff,
  PhoneCall,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Loader2,
  Download,
  Eye,
  Square,
  CheckSquare,
  AlertTriangle,
  UserX,
  UserPlus,
  MapPin,
  Wrench,
  Truck,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import Layout from '../components/Layout';
import SyncProgressBar from '../components/SyncProgressBar';
import { useLocationContext } from '../context/LocationContext';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: MessageSquare },
  scheduled: { label: 'Scheduled', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  no_response: { label: 'No Response', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  no_phone: { label: 'No Phone', color: 'bg-red-100 text-red-700', icon: PhoneOff },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

export default function Reminders() {
  const queryClient = useQueryClient();
  const [weekOffset, setWeekOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [excludingId, setExcludingId] = useState(null);
  const [refreshingCustomerId, setRefreshingCustomerId] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [reminderSort, setReminderSort] = useState(() => localStorage.getItem('reminders_sortField') || 'date');
  const [reminderSortDir, setReminderSortDir] = useState(() => localStorage.getItem('reminders_sortDir') || 'asc');
  const { selectedLocationId, currentLocation } = useLocationContext();

  // Fetch live from Zuper + enrich with local reminder state
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['reminders', weekOffset, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/reminders/live?weekOffset=${weekOffset}`);
      return data;
    },
    refetchOnWindowFocus: false,
  });

  // Fetch send preview
  const { data: previewData, isLoading: previewLoading } = useQuery({
    queryKey: ['reminder-preview', weekOffset, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/reminders/preview?weekOffset=${weekOffset}`);
      return data;
    },
    enabled: showPreview,
  });

  // Send all reminders mutation
  const sendAllMutation = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post('/api/reminders/send', { weekOffset });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      setShowPreview(false);
    },
  });

  // Send single reminder mutation
  const sendSingleMutation = useMutation({
    mutationFn: async (reminder) => {
      const rid = reminder.id || reminder.job_uid;
      setSendingId(rid);
      if (reminder.id) {
        // Has local DB record — use existing endpoint
        const { data } = await axios.post(`/api/reminders/send/${reminder.id}`);
        return data;
      } else {
        // No local record — use send-by-job which creates it
        const { data } = await axios.post(`/api/reminders/send-by-job/${reminder.job_uid}`, {
          customer_uid: reminder.customer_uid,
          customer_name: reminder.customer_name,
          customer_phone: reminder.customer_phone,
          asset_name: reminder.asset_name,
          asset_serial_number: reminder.asset_serial_number,
          job_category: reminder.job_category,
          work_order_number: reminder.work_order_number,
          workshop_location: reminder.workshop_location,
        });
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      showToast('Reminder sent successfully');
    },
    onError: (err) => showToast(err?.response?.data?.error || 'Failed to send reminder', 'error'),
    onSettled: () => {
      setSendingId(null);
    },
  });


  // Exclude customer mutation
  const excludeMutation = useMutation({
    mutationFn: async ({ customerUid, customerName }) => {
      setExcludingId(customerUid);
      const { data } = await axios.post(`/api/reminders/exclude/${customerUid}`, {
        customerName,
        reason: 'Manually excluded from dashboard',
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      queryClient.invalidateQueries({ queryKey: ['excluded-customers'] });
      showToast('Customer excluded');
    },
    onError: (err) => showToast(err?.response?.data?.error || 'Failed to exclude', 'error'),
    onSettled: () => setExcludingId(null),
  });

  // Remove exclusion mutation
  const removeExclusionMutation = useMutation({
    mutationFn: async (customerUid) => {
      setExcludingId(customerUid);
      const { data } = await axios.delete(`/api/reminders/exclude/${customerUid}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      queryClient.invalidateQueries(['excluded-customers']);
      showToast('Customer re-included');
    },
    onError: (err) => showToast(err?.response?.data?.error || 'Failed to re-include', 'error'),
    onSettled: () => setExcludingId(null),
  });

  // Refresh customer data from Zuper — pulls latest phone/name/asset fields
  // for every active reminder owned by this customer. Used after an operator
  // edits a customer in Zuper and wants the AMC system to reflect it.
  const refreshCustomerMutation = useMutation({
    mutationFn: async (customerUid) => {
      setRefreshingCustomerId(customerUid);
      const { data } = await axios.post(`/api/admin/refresh-customer/${customerUid}`);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      if (data.jobsChanged === 0) {
        showToast(
          data.zuperFailures > 0
            ? `Zuper unreachable for ${data.zuperFailures} job(s) — nothing updated`
            : 'Already up to date with Zuper',
          data.zuperFailures > 0 ? 'error' : 'success',
        );
      } else {
        const phoneChanged = data.changesByJob.some((c) => c.phoneChanged);
        const detail = phoneChanged ? ' (incl. phone number)' : '';
        showToast(`Refreshed ${data.jobsChanged} job${data.jobsChanged === 1 ? '' : 's'} from Zuper${detail}`);
      }
    },
    onError: (err) => showToast(err?.response?.data?.error || 'Failed to refresh from Zuper', 'error'),
    onSettled: () => setRefreshingCustomerId(null),
  });

  // Fetch excluded customers
  const { data: excludedData } = useQuery({
    queryKey: ['excluded-customers'],
    queryFn: async () => {
      const { data } = await axios.get('/api/reminders/excluded');
      return data;
    },
  });

  const excludedCustomers = excludedData?.excluded || [];
  const [showExcluded, setShowExcluded] = useState(false);

  const campaign = data?.campaign;
  const stats = campaign?.stats || {};
  const allReminders = data?.reminders || [];

  const handleReminderSort = (field) => {
    if (reminderSort === field) {
      const newDir = reminderSortDir === 'asc' ? 'desc' : 'asc';
      setReminderSortDir(newDir);
      localStorage.setItem('reminders_sortDir', newDir);
    } else {
      setReminderSort(field);
      setReminderSortDir('asc');
      localStorage.setItem('reminders_sortField', field);
      localStorage.setItem('reminders_sortDir', 'asc');
    }
  };

  // Filter and sort reminders — TBC (non-scheduled) first, then by selected sort
  const reminders = useMemo(() => {
    const filtered = statusFilter
      ? allReminders.filter(r => r.status === statusFilter)
      : allReminders;
    const dir = reminderSortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      // Always keep scheduled at the bottom
      const aScheduled = a.status === 'scheduled' ? 1 : 0;
      const bScheduled = b.status === 'scheduled' ? 1 : 0;
      if (aScheduled !== bScheduled) return aScheduled - bScheduled;
      // Then apply user sort
      let cmp = 0;
      switch (reminderSort) {
        case 'name':
          cmp = (a.customer_name || '').localeCompare(b.customer_name || '');
          break;
        case 'status':
          cmp = (a.status || '').localeCompare(b.status || '');
          break;
        case 'followups':
          cmp = (a.reminder_count || 0) - (b.reminder_count || 0);
          break;
        case 'date':
        default:
          cmp = (a.scheduled_date || '').localeCompare(b.scheduled_date || '');
          if (cmp === 0) cmp = (a.scheduled_time || '').localeCompare(b.scheduled_time || '');
          break;
      }
      return cmp * dir;
    });
  }, [allReminders, statusFilter, reminderSort, reminderSortDir]);

  // Week navigation (changing weekOffset triggers refetch automatically)
  const goToPrevWeek = () => { setWeekOffset(w => w - 1); setSendingId(null); };
  const goToNextWeek = () => { setWeekOffset(w => w + 1); setSendingId(null); };
  const goToCurrentWeek = () => { setWeekOffset(0); setSendingId(null); };

  const getWeekLabel = () => {
    if (weekOffset === 0) return 'Current Week';
    if (weekOffset === 1) return 'Next Week';
    if (weekOffset === -1) return 'Last Week';
    if (weekOffset > 0) return `${weekOffset} Weeks Ahead`;
    return `${Math.abs(weekOffset)} Weeks Ago`;
  };

  const formatDateRange = () => {
    if (!campaign) return '';
    const fmt = (iso) => {
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };
    // week_end is exclusive (next Thursday), show inclusive end (Wednesday = end - 1)
    const endDate = new Date(campaign.week_end + 'T00:00:00');
    endDate.setDate(endDate.getDate() - 1);
    const endStr = endDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return `${fmt(campaign.week_start)} - ${endStr}`;
  };

  const formatLastSynced = () => {
    if (!campaign?.last_synced_at) return null;
    // UTC timestamp: ISO-8601 from Postgres, or legacy "YYYY-MM-DD HH:MM:SS".
    const ls = String(campaign.last_synced_at);
    const d = new Date(ls.endsWith('Z') ? ls : ls.replace(' ', 'T') + 'Z');
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const toggleExpanded = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const toggleSelected = (jobUid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(jobUid)) next.delete(jobUid);
      else next.add(jobUid);
      return next;
    });
  };

  // Derive unique categories from reminders
  const categories = useMemo(() => {
    const catMap = {};
    allReminders.forEach(r => {
      const cat = r.job_category || 'Unknown';
      catMap[cat] = (catMap[cat] || 0) + 1;
    });
    return Object.entries(catMap).map(([name, count]) => ({ name, count }));
  }, [allReminders]);

  const getJobColor = (category) => {
    const colors = {
      'AMC - Workshop': 'bg-brand-dark',
      'Collection': 'bg-blue-600',
      'AMC Valet': 'bg-purple-600',
      'Delivery': 'bg-orange-600',
    };
    return colors[category] || 'bg-gray-500';
  };

  const getCategoryIcon = (category) => {
    if (category?.includes('Workshop')) return Wrench;
    return Truck;
  };

  const selectableReminders = reminders.filter(r => r.status !== 'scheduled');
  const allSelected = selectableReminders.length > 0 && selectableReminders.every(r => selectedIds.has(r.job_uid));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableReminders.map(r => r.job_uid)));
    }
  };

  const handleExport = () => {
    const campaignId = campaign?.id;
    const url = campaignId
      ? `/api/reminders/export?campaignId=${campaignId}`
      : '/api/reminders/export';
    window.open(url, '_blank');
  };

  const lastSynced = formatLastSynced();

  return (
    <Layout>
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 animate-in fade-in slide-in-from-top-2 ${
          toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-brand-primary text-white'
        }`}>
          {toast.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm relative">
        <SyncProgressBar isSyncing={isFetching} />
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <Bell className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Booking Reminders</h1>
            <p className="text-xs text-brand-neutral hidden md:block">Manage and send WhatsApp service reminders</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 text-xs md:text-sm font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-2 text-xs md:text-sm font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light transition-colors shadow-sm disabled:opacity-50"
            title="Refresh from Zuper"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{isFetching ? 'Syncing...' : 'Sync'}</span>
          </button>
          <button
            onClick={() => setShowPreview(true)}
            disabled={sendAllMutation.isPending || (stats.pending === 0 && stats.sent === 0)}
            className="flex items-center gap-1.5 px-3 md:px-4 py-2 text-xs md:text-sm font-medium text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:shadow-lg transition-all shadow-sm disabled:opacity-50"
          >
            {sendAllMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">
              {sendAllMutation.isPending ? 'Sending...' : 'Send All'}
            </span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        <div className="max-w-full mx-auto space-y-3 md:space-y-4">
          {/* Alerts */}
          {sendAllMutation.isSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-green-800 text-sm">
              <strong>Reminders sent!</strong> {sendAllMutation.data?.results?.sent || 0} sent, {sendAllMutation.data?.results?.failed || 0} failed.
            </div>
          )}
          {sendAllMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-800 text-sm">
              <strong>Failed to send reminders.</strong> {sendAllMutation.error?.message}
            </div>
          )}

          {/* Send Preview Modal */}
          {showPreview && (
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light overflow-hidden">
              <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light bg-amber-50 flex items-center justify-between">
                <h3 className="text-sm font-bold text-brand-dark flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Send Preview
                </h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-xs text-brand-neutral hover:text-brand-dark"
                >
                  Close
                </button>
              </div>
              <div className="p-4">
                {previewLoading ? (
                  <div className="text-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-brand-primary" />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-4 text-center text-xs">
                      <div className="bg-blue-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-blue-600">{previewData?.willSend || 0}</p>
                        <p className="text-blue-600/70">Will Send</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-red-600">{previewData?.noPhone || 0}</p>
                        <p className="text-red-600/70">No Phone</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-green-600">{previewData?.alreadyScheduled || 0}</p>
                        <p className="text-green-600/70">Already Booked</p>
                      </div>
                    </div>

                    {previewData?.recipients?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-brand-neutral mb-2">Recipients:</p>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {previewData.recipients.map((r, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs bg-brand-light/50 rounded-lg px-3 py-1.5">
                              <Phone className="w-3 h-3 text-brand-neutral" />
                              <span className="font-medium text-brand-dark">{r.customer_name}</span>
                              <span className="text-brand-neutral">{r.customer_phone}</span>
                              {r.reminder_count > 0 && (
                                <span className="text-amber-600 ml-auto">Follow-up #{r.reminder_count + 1}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setShowPreview(false)}
                        className="px-4 py-2 text-xs font-medium text-brand-dark bg-brand-light rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => sendAllMutation.mutate()}
                        disabled={sendAllMutation.isPending || !previewData?.willSend}
                        className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                      >
                        {sendAllMutation.isPending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                        Confirm Send ({previewData?.willSend || 0})
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Week Navigation & Stats */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 p-3 md:p-4">
            {/* Mobile: Stacked Layout */}
            <div className="flex flex-col gap-3 md:hidden">
              {/* Week Selector */}
              <div className="flex items-center justify-between">
                <button
                  onClick={goToPrevWeek}
                  className="p-2 rounded-xl hover:bg-brand-light text-brand-dark transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="text-center flex-1">
                  <h2 className="text-base font-bold text-brand-dark">{getWeekLabel()}</h2>
                  <p className="text-xs text-brand-neutral">{formatDateRange()}</p>
                </div>

                <button
                  onClick={goToNextWeek}
                  className="p-2 rounded-xl hover:bg-brand-light text-brand-dark transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {weekOffset !== 0 && (
                <button
                  onClick={goToCurrentWeek}
                  className="w-full px-3 py-1.5 text-xs font-medium text-brand-primary bg-brand-light rounded-lg hover:bg-brand-primary hover:text-white transition-colors"
                >
                  Back to Current Week
                </button>
              )}

              {/* Stats Row */}
              <div className="grid grid-cols-4 gap-2">
                <StatCard
                  label="Total"
                  value={stats.total || 0}
                  color="bg-gray-50"
                  active={statusFilter === null}
                  onClick={() => setStatusFilter(null)}
                />
                <StatCard
                  label="Pending"
                  value={stats.pending || 0}
                  color="bg-blue-50 text-blue-600"
                  active={statusFilter === 'pending'}
                  onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
                />
                <StatCard
                  label="Sent"
                  value={stats.sent || 0}
                  color="bg-amber-50 text-amber-600"
                  active={statusFilter === 'sent'}
                  onClick={() => setStatusFilter(statusFilter === 'sent' ? null : 'sent')}
                />
                <StatCard
                  label="Scheduled"
                  value={stats.scheduled || 0}
                  color="bg-green-50 text-green-600"
                  active={statusFilter === 'scheduled'}
                  onClick={() => setStatusFilter(statusFilter === 'scheduled' ? null : 'scheduled')}
                />
              </div>
            </div>

            {/* Desktop: Horizontal Layout */}
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={goToPrevWeek}
                  className="p-2 rounded-xl hover:bg-brand-light text-brand-dark transition-colors"
                  title="Previous week"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="text-center min-w-[200px]">
                  <h2 className="text-lg font-bold text-brand-dark">{getWeekLabel()}</h2>
                  <p className="text-sm text-brand-neutral">{formatDateRange()}</p>
                </div>

                <button
                  onClick={goToNextWeek}
                  className="p-2 rounded-xl hover:bg-brand-light text-brand-dark transition-colors"
                  title="Next week"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {weekOffset !== 0 && (
                  <button
                    onClick={goToCurrentWeek}
                    className="ml-2 px-3 py-1.5 text-xs font-medium text-brand-primary bg-brand-light rounded-lg hover:bg-brand-primary hover:text-white transition-colors"
                  >
                    Today
                  </button>
                )}
              </div>

              <div className="flex items-center gap-4">
                <StatCard
                  label="Total"
                  value={stats.total || 0}
                  color="bg-gray-50"
                  active={statusFilter === null}
                  onClick={() => setStatusFilter(null)}
                />
                <StatCard
                  label="Pending"
                  value={stats.pending || 0}
                  color="bg-blue-50 text-blue-600"
                  active={statusFilter === 'pending'}
                  onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
                />
                <StatCard
                  label="Sent"
                  value={stats.sent || 0}
                  color="bg-amber-50 text-amber-600"
                  active={statusFilter === 'sent'}
                  onClick={() => setStatusFilter(statusFilter === 'sent' ? null : 'sent')}
                />
                <StatCard
                  label="Scheduled"
                  value={stats.scheduled || 0}
                  color="bg-green-50 text-green-600"
                  active={statusFilter === 'scheduled'}
                  onClick={() => setStatusFilter(statusFilter === 'scheduled' ? null : 'scheduled')}
                />
              </div>
            </div>

            {/* Category Legend - Hidden on mobile */}
            {categories.length > 0 && (
              <div className="hidden md:flex items-center gap-4 mt-4 pt-4 border-t border-brand-light">
                <span className="text-xs text-brand-neutral font-medium">Category:</span>
                <div className="flex gap-4">
                  {categories.map(({ name, count }) => {
                    const CatIcon = getCategoryIcon(name);
                    return (
                      <span key={name} className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded ${getJobColor(name)}`}></span>
                        <CatIcon className="w-3 h-3 text-brand-dark" />
                        <span className="text-xs text-brand-dark">
                          {name || 'Unknown'}
                        </span>
                        <span className="text-[10px] text-brand-neutral">({count})</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Excluded Customers Toggle */}
          {excludedCustomers.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light overflow-hidden">
              <button
                onClick={() => setShowExcluded(!showExcluded)}
                className="w-full px-4 md:px-6 py-3 flex items-center justify-between hover:bg-brand-light/30 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <UserX className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium text-brand-dark">
                    Excluded Customers ({excludedCustomers.length})
                  </span>
                </div>
                {showExcluded ? <ChevronUp className="w-4 h-4 text-brand-neutral" /> : <ChevronDown className="w-4 h-4 text-brand-neutral" />}
              </button>
              {showExcluded && (
                <div className="border-t border-brand-light divide-y divide-brand-light/50">
                  {excludedCustomers.map((ec) => (
                    <div key={ec.customer_uid} className="px-4 md:px-6 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-brand-dark">{ec.customer_name || ec.customer_uid}</p>
                        <p className="text-xs text-brand-neutral">
                          {ec.reason && <span>{ec.reason} &middot; </span>}
                          Excluded by {ec.excluded_by || 'system'}
                        </p>
                      </div>
                      <button
                        onClick={() => removeExclusionMutation.mutate(ec.customer_uid)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        <UserPlus className="w-3 h-3" />
                        Include
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Reminders List */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Select All checkbox */}
                {selectableReminders.length > 0 && (
                  <button onClick={toggleSelectAll} className="text-brand-neutral hover:text-brand-primary transition-colors">
                    {allSelected ? (
                      <CheckSquare className="w-4 h-4 text-brand-primary" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                )}
                <h3 className="text-sm font-bold text-brand-dark">
                  Reminders {!isLoading && `(${reminders.length})`}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {statusFilter && (
                  <button
                    onClick={() => setStatusFilter(null)}
                    className="text-xs text-brand-primary hover:underline"
                  >
                    Clear filter
                  </button>
                )}
                <select
                  value={reminderSort}
                  onChange={(e) => handleReminderSort(e.target.value)}
                  className="text-[11px] border border-brand-light rounded-lg px-2 py-1 bg-white text-brand-dark cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-primary/30"
                >
                  <option value="date">Sort by Date</option>
                  <option value="name">Sort by Name</option>
                  <option value="status">Sort by Status</option>
                  <option value="followups">Sort by Follow-ups</option>
                </select>
                <button
                  onClick={() => {
                    const newDir = reminderSortDir === 'asc' ? 'desc' : 'asc';
                    setReminderSortDir(newDir);
                    localStorage.setItem('reminders_sortDir', newDir);
                  }}
                  className="p-1 text-brand-neutral hover:text-brand-primary transition-colors"
                  title={reminderSortDir === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {reminderSortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {isLoading ? (
              <div className="p-8 text-center text-brand-neutral">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-brand-primary" />
                <p className="text-sm">Loading reminders...</p>
              </div>
            ) : reminders.length === 0 ? (
              <div className="p-8 text-center text-brand-neutral">
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">
                  {statusFilter ? `No ${statusFilter} reminders` : 'No reminders for this week'}
                </p>
                <button
                  onClick={() => refetch()}
                  className="mt-4 px-4 py-2 text-sm font-medium text-brand-primary bg-brand-light rounded-xl hover:bg-brand-primary hover:text-white transition-colors"
                >
                  Sync from Zuper
                </button>
              </div>
            ) : (
              <div className="divide-y divide-brand-light/50">
                {reminders.map((reminder) => (
                  <ReminderRow
                    key={reminder.id || reminder.job_uid}
                    reminder={reminder}
                    onSend={() => sendSingleMutation.mutate(reminder)}
                    onExclude={() => excludeMutation.mutate({ customerUid: reminder.customer_uid, customerName: reminder.customer_name })}
                    onInclude={() => removeExclusionMutation.mutate(reminder.customer_uid)}
                    onRefresh={() => refreshCustomerMutation.mutate(reminder.customer_uid)}
                    isExcluded={excludedCustomers.some(ec => ec.customer_uid === reminder.customer_uid)}
                    isSending={sendingId != null && (sendingId === reminder.id || sendingId === reminder.job_uid)}
                    isExcluding={excludingId === reminder.customer_uid}
                    isRefreshing={refreshingCustomerId === reminder.customer_uid}
                    isExpanded={expandedId === (reminder.id || reminder.job_uid)}
                    onToggle={() => toggleExpanded(reminder.id || reminder.job_uid)}
                    isSelected={selectedIds.has(reminder.job_uid)}
                    onToggleSelect={() => toggleSelected(reminder.job_uid)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </Layout>
  );
}

function StatCard({ label, value, color, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-center px-2 md:px-4 py-2.5 rounded-xl border transition-all hover:shadow-sm ${color} ${active ? 'ring-2 ring-brand-primary ring-offset-1 border-brand-primary/20' : 'border-transparent'
        }`}
    >
      <p className="text-lg md:text-2xl font-bold">{value}</p>
      <p className="text-[10px] md:text-xs font-medium opacity-70 uppercase tracking-wider">{label}</p>
    </button>
  );
}

function ReminderRow({ reminder, onSend, onExclude, onInclude, onRefresh, isExcluded, isSending, isExcluding, isRefreshing, isExpanded, onToggle, isSelected, onToggleSelect }) {
  const statusConfig = STATUS_CONFIG[reminder.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  // Calculate days since last reminder (unresponsive badge)
  const getDaysSinceLastReminder = () => {
    if (!reminder.last_reminder_sent_at || reminder.status === 'scheduled') return null;
    const ls = String(reminder.last_reminder_sent_at);
    const lastSent = new Date(ls.endsWith('Z') ? ls : ls.replace(' ', 'T') + 'Z');
    const now = new Date();
    const diffMs = now - lastSent;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysSinceReminder = getDaysSinceLastReminder();
  const needsCall = reminder.reminder_count >= 3 && reminder.status !== 'scheduled';
  const isUnresponsive = daysSinceReminder !== null && daysSinceReminder >= 1 && reminder.status !== 'scheduled';

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Never';
    const s = isoString.endsWith('Z') ? isoString : isoString.replace(' ', 'T') + 'Z';
    const d = new Date(s);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className={`transition-all duration-150 ${isExcluded ? 'bg-gray-50' : 'hover:bg-brand-light/20'}`}>
      {/* Main Row - Clickable */}
      <div className="p-4 cursor-pointer flex items-start gap-3" onClick={onToggle}>
        {/* Checkbox */}
        {reminder.status !== 'scheduled' && (
          <button
            className={`mt-1 transition-colors flex-shrink-0 ${isExcluded ? 'text-gray-300' : 'text-brand-neutral hover:text-brand-primary'}`}
            onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          >
            {isSelected ? (
              <CheckSquare className="w-4 h-4 text-brand-primary" />
            ) : (
              <Square className="w-4 h-4" />
            )}
          </button>
        )}
        {reminder.status === 'scheduled' && <div className="w-4 flex-shrink-0" />}

        <div className="flex-1 min-w-0">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            {/* Customer Info */}
            <div className={`flex-1 min-w-0 ${isExcluded ? 'opacity-40' : ''}`}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium ${statusConfig.color}`}>
                  <StatusIcon className="w-3 h-3" />
                  {statusConfig.label}
                </span>
                {reminder.reminder_count > 0 && (
                  <span className="text-[10px] text-brand-neutral">
                    {reminder.reminder_count}x sent
                  </span>
                )}
                {/* Needs Call badge */}
                {needsCall && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium bg-orange-100 text-orange-700">
                    <PhoneCall className="w-3 h-3" />
                    Needs Call
                  </span>
                )}
                {/* Unresponsive badge */}
                {isUnresponsive && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium bg-red-50 text-red-600">
                    <AlertTriangle className="w-3 h-3" />
                    {daysSinceReminder}d no response
                  </span>
                )}
                <span className="ml-auto md:hidden">
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-brand-neutral" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-brand-neutral" />
                  )}
                </span>
              </div>
              <p className="font-medium text-brand-dark truncate">
                {reminder.customer_name}
              </p>
              {reminder.asset_name && (
                <p className="text-xs text-brand-neutral mt-0.5 truncate">
                  {reminder.asset_name}
                  {reminder.asset_serial_number && (
                    <span className="font-mono ml-1.5 text-[10px] opacity-70">SN: {reminder.asset_serial_number}</span>
                  )}
                </p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-brand-neutral mt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {reminder.scheduled_date || 'No date'}
                </span>
                {reminder.scheduled_time && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {reminder.scheduled_time}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  {reminder.customer_phone ? (
                    <>
                      <Phone className="w-3 h-3" />
                      {reminder.customer_phone}
                    </>
                  ) : (
                    <>
                      <PhoneOff className="w-3 h-3 text-red-500" />
                      No phone
                    </>
                  )}
                </span>
                {reminder.workshop_location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {reminder.workshop_location}
                  </span>
                )}
              </div>
              {/* Last Sent / Next Reminder inline (UAT 7) */}
              {reminder.status !== 'scheduled' && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-brand-neutral mt-1">
                  <span>
                    Last Sent: <span className="font-medium text-brand-dark">{reminder.last_reminder_sent_at ? formatDateTime(reminder.last_reminder_sent_at) : 'Never'}</span>
                  </span>
                  <span>
                    Next Reminder: <span className="font-medium text-brand-dark">{reminder.next_reminder_date || 'Next scheduled run'}</span>
                  </span>
                </div>
              )}
            </div>

            {/* Work Order & Category & Actions */}
            <div className="flex items-center gap-4 text-xs">
              <div className={`text-right hidden md:block ${isExcluded ? 'opacity-40' : ''}`}>
                <p className="font-mono text-brand-neutral">#{reminder.work_order_number || 'N/A'}</p>
                <p className="text-brand-neutral truncate max-w-[150px]">
                  {reminder.job_category || 'N/A'}
                </p>
                {reminder.workshop_location && (
                  <p className="flex items-center justify-end gap-1 mt-0.5 text-[11px] text-brand-neutral">
                    <MapPin className="w-3 h-3" />
                    {reminder.workshop_location}
                  </p>
                )}
              </div>

              {/* Expand indicator - desktop */}
              <span className="hidden md:block">
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-brand-neutral" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-brand-neutral" />
                )}
              </span>

              {/* Actions */}
              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                {/* Refresh from Zuper — pull latest phone/name/asset for this customer */}
                {reminder.customer_uid && (
                  <button
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    title="Refresh this customer's details from Zuper (phone, name, asset)"
                    className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-brand-neutral bg-brand-light rounded-lg hover:bg-brand-primary hover:text-white transition-colors disabled:opacity-50"
                  >
                    {isRefreshing ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3" />
                    )}
                    <span className="hidden sm:inline">Refresh</span>
                  </button>
                )}
                {reminder.status !== 'scheduled' && reminder.customer_phone && (
                  <button
                    onClick={onSend}
                    disabled={isSending}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-brand-primary rounded-lg hover:bg-brand-secondary transition-colors disabled:opacity-50"
                  >
                    {isSending ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Send className="w-3 h-3" />
                    )}
                    Send
                  </button>
                )}
                {isExcluded ? (
                  <button
                    onClick={onInclude}
                    disabled={isExcluding}
                    title="Re-include this customer in automated reminders"
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 hover:text-green-700 transition-colors disabled:opacity-50"
                  >
                    {isExcluding ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                    <span className="hidden sm:inline">Include</span>
                  </button>
                ) : (
                  <button
                    onClick={onExclude}
                    disabled={isExcluding}
                    title="Exclude this customer from automated reminders"
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-500 bg-red-50 rounded-lg hover:bg-red-100 hover:text-red-700 transition-colors disabled:opacity-50"
                  >
                    {isExcluding ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserX className="w-3 h-3" />}
                    <span className="hidden sm:inline">Exclude</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Detail Panel */}
      {isExpanded && (
        <ReminderDetail reminder={reminder} />
      )}
    </div>
  );
}

function ReminderDetail({ reminder }) {
  const queryClient = useQueryClient();

  // Fetch message history when expanded
  const { data, isLoading } = useQuery({
    queryKey: ['reminder-messages', reminder.id],
    queryFn: async () => {
      const { data } = await axios.get(`/api/reminders/${reminder.id}/messages`);
      return data;
    },
  });

  // Refresh delivery status from DoubleTick
  const refreshMutation = useMutation({
    mutationFn: () => axios.post('/api/admin/refresh-message-status', { reminderId: reminder.id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reminder-messages', reminder.id] }),
  });

  const messages = data?.messages || [];

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const s = isoString.endsWith('Z') ? isoString : isoString.replace(' ', 'T') + 'Z';
    const d = new Date(s);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Build day-of-week status from message history
  // Campaign week: Thu-Wed
  const DAYS = ['Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed'];
  const DAY_INDEX_MAP = { Thu: 4, Fri: 5, Sat: 6, Sun: 0, Mon: 1, Tue: 2, Wed: 3 };
  const getDaySentStatus = () => {
    const sentDays = {};
    const failedDays = {};
    for (const msg of messages) {
      if (!msg.sent_at) continue;
      const s = msg.sent_at.endsWith('Z') ? msg.sent_at : msg.sent_at.replace(' ', 'T') + 'Z';
      const d = new Date(s);
      const jsDay = d.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
      if (msg.status === 'failed') {
        failedDays[jsDay] = true;
      } else {
        sentDays[jsDay] = true;
      }
    }
    return DAYS.map((label) => {
      const jsDay = DAY_INDEX_MAP[label];
      return {
        label,
        sent: !!sentDays[jsDay],
        failed: !sentDays[jsDay] && !!failedDays[jsDay],
      };
    });
  };

  const dayStatuses = getDaySentStatus();

  return (
    <div className="px-4 pb-4 border-t border-brand-light/50 bg-brand-light/20">
      <div className="pt-3 space-y-3">
        {/* Day-of-Week Reminder Status Bar */}
        <div className="bg-white rounded-lg p-3 border border-brand-light">
          <p className="text-[10px] text-brand-neutral font-medium mb-2">Reminder Status by Day</p>
          <div className="flex items-center">
            {dayStatuses.map((day, i) => (
              <div key={day.label} className="flex items-center flex-1 last:flex-none">
                {/* Node */}
                <div className="flex flex-col items-center">
                  <div
                    className={`relative z-10 w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-[10px] md:text-xs font-medium transition-colors ${day.sent
                      ? 'bg-green-500 text-white'
                      : day.failed
                        ? 'bg-red-400 text-white'
                        : 'bg-gray-100 text-gray-400'
                      }`}
                  >
                    {day.sent ? (
                      <CheckCircle className="w-3.5 h-3.5" />
                    ) : day.failed ? (
                      <AlertCircle className="w-3.5 h-3.5" />
                    ) : null}
                  </div>
                  <p className={`text-[9px] md:text-[10px] mt-1 font-medium ${day.sent ? 'text-green-600' : day.failed ? 'text-red-500' : 'text-gray-400'
                    }`}>
                    {day.label}
                  </p>
                </div>
                {/* Connecting line */}
                {i < dayStatuses.length - 1 && (
                  <div className={`flex-1 h-0.5 -mt-4 mx-0.5 ${day.sent && dayStatuses[i + 1].sent
                    ? 'bg-green-500'
                    : day.sent || dayStatuses[i + 1].sent
                      ? 'bg-green-200'
                      : 'bg-gray-200'
                    }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Summary Info (only non-duplicated fields) */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          <div className="bg-white rounded-lg p-2 border border-brand-light">
            <p className="text-brand-neutral mb-0.5">Follow-ups</p>
            <p className="font-bold text-brand-dark text-sm">{reminder.reminder_count || 0}</p>
          </div>
          <div className="bg-white rounded-lg p-2 border border-brand-light">
            <p className="text-brand-neutral mb-0.5">Last Sent</p>
            <p className="font-medium text-brand-dark">{formatDateTime(reminder.last_reminder_sent_at)}</p>
          </div>
          <div className="bg-white rounded-lg p-2 border border-brand-light">
            <p className="text-brand-neutral mb-0.5">Workshop Location</p>
            <p className="font-medium text-brand-dark flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {reminder.workshop_location || 'Not assigned'}</p>
          </div>
        </div>

        {/* Message History */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-brand-dark">Message History</h4>
            {messages.length > 0 && (
              <button
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending}
                className="flex items-center gap-1 text-[10px] text-brand-primary hover:text-brand-secondary transition-colors disabled:opacity-50"
                title="Refresh delivery status from DoubleTick"
              >
                <RefreshCw className={`w-3 h-3 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
                {refreshMutation.isPending ? 'Checking...' : 'Refresh status'}
              </button>
            )}
          </div>
          {isLoading ? (
            <div className="text-center py-3">
              <Loader2 className="w-4 h-4 animate-spin mx-auto text-brand-primary" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-xs text-brand-neutral py-2">No messages sent yet</p>
          ) : (
            <div className="space-y-1.5">
              {messages.map((msg, i) => (
                <div key={msg.id || i} className="flex items-center gap-3 bg-white rounded-lg p-2 border border-brand-light text-xs">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${msg.status === 'failed' ? 'bg-red-500' :
                    msg.status === 'read' ? 'bg-emerald-500' :
                      msg.status === 'delivered' ? 'bg-green-500' :
                        'bg-blue-400'
                    }`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-brand-dark font-medium">
                      {msg.template_name || 'amc_reminder'}
                    </span>
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-medium ${msg.status === 'failed' ? 'bg-red-100 text-red-600' :
                      msg.status === 'read' ? 'bg-emerald-100 text-emerald-700' :
                        msg.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          'bg-blue-50 text-blue-600'
                      }`}
                      title={msg.status === 'sent' ? 'Accepted by DoubleTick — delivery pending. Click "Refresh status" to check.' : ''}
                    >
                      {msg.status === 'sent' ? 'enqueued' : msg.status}
                    </span>
                    {msg.phone_number && (
                      <span className="text-brand-neutral ml-2">{msg.phone_number}</span>
                    )}
                    {msg.error_message && (
                      <span className="text-red-500 ml-2 truncate">{msg.error_message}</span>
                    )}
                  </div>
                  <span className="text-brand-neutral flex-shrink-0">{formatDateTime(msg.sent_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
